import { computeNumLicensesUsed, getProductVolume, formatBytes } from '@localstack/services';
import {
  Subscription,
  ProductType,
  Product,
  UsageSummary,
  OrderContext,
  LicenseAssignment,
  ComputeUsageSummary,
} from '@localstack/types';

import { Stack, Typography } from '@mui/material';

import { UsageMetric } from './metricColumns';

export type SubscriptionDetailsType = {
  usageData: UsageSummary;
  licenseAssignments?: LicenseAssignment[] | null;
  computeUsage?: ComputeUsageSummary | null;
};

const getProductUsage = (product: Product, usage: Optional<UsageSummary>): number => {
  if (!product) {
    return 0;
  }
  const productUsage = usage?.usage_by_product[product.id];
  return productUsage ?? 0;
};

export const SubscriptionDetails = ({
  subscription,
  subscriptionDetails: subscriptionDetails,
  columns,
}: {
  subscription: Subscription;
  subscriptionDetails: SubscriptionDetailsType;
  columns: UsageMetric[];
}): JSX.Element => {
  const context: OrderContext = { seats: subscription.seats, org_id: '', customer_id: '' };

  const licensesUsed =
    subscription && subscriptionDetails.licenseAssignments
      ? computeNumLicensesUsed(subscription, subscriptionDetails.licenseAssignments)
      : undefined;

  const usedCredits = subscriptionDetails.computeUsage?.usage.per_sub[subscription.id]?.compute_credits ?? 0;

  const products = subscription.products.reduce(
    (dict, product) => ({ ...dict, [product.type]: product }),
    {} as Record<ProductType, Product>,
  );

  return (
    <>
      {columns.map(({ type: productType, title }) => {
        if (!(productType in products)) {
          return <span key={productType} />;
        }

        const product = products[productType];

        let usageValue, volumeValue;

        switch (productType) {
          case ProductType.COMPUTE_USAGE:
            usageValue = usedCredits;
            volumeValue = getProductVolume(product, context);
            break;
          case ProductType.POD_USAGE:
            usageValue = formatBytes(getProductUsage(product, subscriptionDetails.usageData), 0);
            volumeValue = formatBytes(getProductVolume(product, context), 0);
            break;
          case ProductType.CI_USAGE:
            usageValue = getProductUsage(product, subscriptionDetails.usageData);
            volumeValue = getProductVolume(product, context);
            break;
          case ProductType.SEATS:
            usageValue = licensesUsed;
            volumeValue = subscription.seats;
            break;
        }

        return (
          <Stack direction="column" spacing="4px" alignItems="end" key={productType}>
            <Typography color="text.tertiary" fontWeight={500}>
              {title}
            </Typography>
            <span style={{ display: 'inline-flex' }}>
              <Typography color="text.primary">{usageValue ?? 0}</Typography>
              <Typography color="text.tertiary">&nbsp;/&nbsp;{volumeValue ?? '∞'}</Typography>
            </span>
          </Stack>
        );
      })}
    </>
  );
};
